import { FormEvent, useEffect, useState } from 'react';
import { backendApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatCard } from '../components/Icon';

interface Invite {
  id: string; token: string; name: string | null; email: string | null;
  credits: number; status: string; created_at: string;
  inviteUrl?: string | null;
  used_by_email?: string | null; used_by_name?: string | null;
  used_by_credits_used?: number | null; used_by_credits_remaining?: number | null;
}

// The invite URL comes from the BACKEND (appUrl(), set per environment), because
// the customer-app host is not derivable from the admin origin: portal.wpos.ai →
// app.wpos.ai but portal.dev.wpos.ai → dev.app.wpcursor.com (a different base
// domain). So we never guess it here — we use the server-supplied inviteUrl.
//
// The one exception is a local dev machine, where there is no server URL and the
// customer app is a known local port. Everywhere else, no server URL means the
// link is genuinely unknown and we say so rather than paste a wrong host.
function localSignupUrl(token: string): string {
  const origin = window.location.origin;
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) {
    return `${origin.replace(/:\d+$/, ':3011')}/signup?invite=${token}`;
  }
  return '';
}

export default function Invites() {
  const { can } = useAuth();
  const canWrite = can('invites', 'write');
  const [invites, setInvites] = useState<Invite[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const d = await backendApi.get<{ invites: Invite[] }>('/license/admin/invites');
      setInvites(d.invites); setError('');
    } catch (err: any) {
      setError((err as ApiError).isForbidden ? 'You do not have access to invites.' : err.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  const used = invites.filter((i) => i.status === 'used').length;
  const pending = invites.filter((i) => i.status === 'pending').length;
  // Expire is the only action, and only a pending invite can be expired: with
  // nothing to act on the column would be empty, so it is not drawn at all.
  const showActions = canWrite && pending > 0;
  const creditsGiven = invites.reduce((s, i) => s + (Number(i.credits) || 0), 0);

  async function revoke(inv: Invite) {
    setError('');
    try { await backendApi.delete(`/license/admin/invites/${inv.id}`); await load(); }
    catch (err: any) { setError(err.message); }
  }

  return (
    <>
      <header className="page-head">
        <div><h1>Pilot Invites</h1><p className="muted">Create and manage invite-only pilot access.</p></div>
      </header>

      <div className="stat-row">
        <StatCard icon="mail" tint="purple" value={invites.length} label="Total Invites" />
        <StatCard icon="check" tint="green" value={used} label="Used" />
        <StatCard icon="clock" tint="amber" value={pending} label="Pending" />
        <StatCard icon="dollar" tint="purple" value={creditsGiven.toLocaleString()} label="Credits Given" />
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {canWrite && <CreateInvite onDone={load} />}

      <div className="card">
        <h2 className="card-title">All Invites</h2>
        {loading ? <p className="muted">Loading…</p> : invites.length === 0 ? <p className="muted">No invites yet.</p> : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Email</th><th>Credits</th><th>Status</th><th>Created</th><th>Invite Link</th>{showActions && <th>Actions</th>}</tr></thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.name || '—'}</td>
                    <td>{inv.email || '—'}
                      {inv.used_by_email && inv.used_by_email !== inv.email &&
                        <span className="muted small block">claimed by {inv.used_by_email}</span>}
                    </td>
                    <td className="mono">{inv.credits}
                      {inv.status === 'used' && inv.used_by_credits_used != null &&
                        <span className="muted small block">{inv.used_by_credits_used} used · {inv.used_by_credits_remaining} left</span>}
                    </td>
                    <td><StatusBadge status={inv.status} /></td>
                    <td className="muted">{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td><InviteLink invite={inv} /></td>
                    {showActions && <td>{inv.status === 'pending' &&
                      <button className="btn btn-sm btn-danger" onClick={() => revoke(inv)}>Expire</button>}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'used' ? 'badge-green' : status === 'pending' ? 'badge-amber' : 'badge-gray';
  return <span className={'badge ' + cls}>{status}</span>;
}

function InviteLink({ invite }: { invite: Invite }) {
  const [copied, setCopied] = useState(false);
  if (invite.status === 'used') return <span className="badge badge-green">Claimed</span>;
  if (invite.status !== 'pending') return <span className="badge badge-gray">Expired</span>;
  // The backend owns the URL (appUrl(), correct per environment). Local dev has
  // no server host, so it gets the known local port; anything else with no server
  // URL is an un-updated backend — we say the link is unavailable rather than
  // paste a host we'd have to guess.
  const url = invite.inviteUrl || localSignupUrl(invite.token);
  if (!url) return <span className="badge badge-gray" title="Update the backend to enable copy links">Link n/a</span>;
  return (
    <button className="link-pill" title={url}
      onClick={() => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
      {copied ? 'Copied!' : 'Copy Link'}
    </button>
  );
}

function CreateInvite({ onDone }: { onDone: () => void }) {
  const [form, setForm] = useState({ name: '', email: '', credits: '100' });
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault(); setError(''); setNote(''); setBusy(true);
    try {
      const res = await backendApi.post<any>('/license/admin/invites', {
        name: form.name.trim() || undefined, email: form.email.trim() || undefined, credits: Number(form.credits),
      });
      // Copy the new invite link. Prefer a server-supplied URL, else build it.
      const token = res?.invite?.token ?? res?.token;
      const url = res?.invite?.inviteUrl ?? res?.inviteUrl ??
        (token ? localSignupUrl(token) : '');
      let copied = false;
      if (url) { try { await navigator.clipboard.writeText(url); copied = true; } catch { /* ignore */ } }
      const emailNote = res?.emailSent ? ` — email sent to ${res?.invite?.email ?? form.email.trim()}` : '';
      setNote((copied ? 'Invite created — link copied to clipboard' : 'Invite created') + emailNote);
      setForm({ name: '', email: '', credits: '100' }); onDone();
    } catch (err: any) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="card" onSubmit={submit}>
      <h2 className="card-title">Create Invite</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {note && <div className="alert alert-ok">{note}</div>}
      <div className="form-row">
        <label className="field"><span>Name (optional)</span>
          <input placeholder="e.g., John Doe" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label className="field"><span>Email (optional)</span>
          <input type="email" placeholder="e.g., john@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="field"><span>Credits</span>
          <input type="number" value={form.credits} onChange={(e) => setForm({ ...form, credits: e.target.value })} /></label>
      </div>
      <button className="btn btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create Invite'}</button>
    </form>
  );
}
